export interface RuleDef {
  id: string;
  name: string;
  global?: boolean | null;
  criteria?: string | null;
  weeklyOutsLimit?: number | null;
}

export interface RuleProfile {
  id: string;
  restRuleId: string;
  pitchCount: number;
  daysRest: number;
}

export interface TeamLite {
  id: string;
  name: string;
  leagueId?: string | null;
  organizationId?: string | null;
}

export interface LeagueLite {
  id: string;
  name: string;
  organizationId: string;
  restRuleId?: string | null;
}

export interface OrganizationLite {
  id: string;
  name: string;
  restRuleId?: string | null;
}

export interface PlayerLite {
  id: string;
  name: string;
  teamId: string;
}

export interface PitchLogLite {
  id: string;
  playerId: string;
  teamId?: string | null;
  playerRosterTeamId?: string | null;
  date: string;
  pitches: number;
  outsRecorded?: number | null;
}

export interface TeamPlayerLite {
  id: string;
  teamID: string;
  playerID: string;
}

export interface PlayerViolation {
  id: string;
  type: "PITCH_LIMIT" | "WEEKLY_OUTS" | "REST_INTERVAL";
  ruleId: string;
  ruleName: string;
  source: string;
  occurredOn: string;
  message: string;
  logId?: string | null;
  teamId?: string | null;
  teamName?: string | null;
}

export interface PlayerAvailability {
  playerId: string;
  playerName: string;
  teamName: string;
  available: boolean;
  availableOn: string;
  reason: string;
  daysRemaining: number;
  restTotalDays: number;
  restProgressPct: number;
  violations: PlayerViolation[];
}

const parseDate = (value: string): Date => {
  const [year, month, day] = value.split("-").map((part) => Number(part));
  return new Date(year, (month ?? 1) - 1, day ?? 1);
};

const formatDate = (value: Date): string => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (value: Date, days: number): Date => {
  const out = new Date(value.getTime());
  out.setDate(out.getDate() + days);
  return out;
};
const calendarDayDiff = (from: Date, to: Date): number => {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  const ms = end.getTime() - start.getTime();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
};

const startOfCalendarWeek = (value: Date): Date => {
  const out = new Date(value.getTime());
  const day = out.getDay();
  const diff = -day;
  out.setDate(out.getDate() + diff);
  out.setHours(0, 0, 0, 0);
  return out;
};

const endOfCalendarWeek = (value: Date): Date => {
  const start = startOfCalendarWeek(value);
  return addDays(start, 6);
};

const includesPitches = (criteria?: string | null): boolean => {
  const normalized = (criteria ?? "BOTH").toUpperCase();
  return normalized === "PITCHES" || normalized === "BOTH";
};

const includesOuts = (criteria?: string | null): boolean => {
  const normalized = (criteria ?? "BOTH").toUpperCase();
  return normalized === "OUTS" || normalized === "BOTH";
};

const EXTERNAL_TEAM_PREFIX = "external:";

const getDisplayTeamName = (teamId: string | null | undefined, teamById: Map<string, TeamLite>): string => {
  if (!teamId) {
    return "Unknown team";
  }
  if (teamId.startsWith(EXTERNAL_TEAM_PREFIX)) {
    const externalName = teamId.slice(EXTERNAL_TEAM_PREFIX.length).trim();
    return externalName ? `${externalName} (External)` : "External team (External)";
  }
  return teamById.get(teamId)?.name ?? "Unknown team";
};

export const evaluatePitcherAvailability = (args: {
  players: PlayerLite[];
  teams: TeamLite[];
  leagues: LeagueLite[];
  organizations: OrganizationLite[];
  rules: RuleDef[];
  profiles: RuleProfile[];
  logs: PitchLogLite[];
  teamPlayers?: TeamPlayerLite[];
}): PlayerAvailability[] => {
  const { players, teams, leagues, organizations, rules, profiles, logs, teamPlayers = [] } = args;
  const teamById = new Map(teams.map((team) => [team.id, team]));
  const leagueById = new Map(leagues.map((league) => [league.id, league]));
  const orgById = new Map(organizations.map((org) => [org.id, org]));
  const ruleById = new Map(rules.map((rule) => [rule.id, rule]));
  const profilesByRuleId = new Map<string, RuleProfile[]>();

  for (const profile of profiles) {
    const list = profilesByRuleId.get(profile.restRuleId) ?? [];
    list.push(profile);
    profilesByRuleId.set(profile.restRuleId, list);
  }

  for (const list of profilesByRuleId.values()) {
    list.sort((a, b) => a.pitchCount - b.pitchCount);
  }

  const logsByPlayerId = new Map<string, PitchLogLite[]>();
  for (const log of logs) {
    const list = logsByPlayerId.get(log.playerId) ?? [];
    list.push(log);
    logsByPlayerId.set(log.playerId, list);
  }

  for (const list of logsByPlayerId.values()) {
    list.sort((a, b) => b.date.localeCompare(a.date));
  }

  const teamIdsByPlayerId = new Map<string, Set<string>>();
  for (const player of players) {
    teamIdsByPlayerId.set(player.id, new Set<string>([player.teamId]));
  }
  for (const link of teamPlayers) {
    const list = teamIdsByPlayerId.get(link.playerID) ?? new Set<string>();
    list.add(link.teamID);
    teamIdsByPlayerId.set(link.playerID, list);
  }

  const today = new Date();
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const weekStart = startOfCalendarWeek(today);
  const weekEnd = endOfCalendarWeek(today);

  return players.map((player) => {
    const team = teamById.get(player.teamId);
    const playerLogs = logsByPlayerId.get(player.id) ?? [];
    const latestLog = playerLogs[0];

    const attachRuleToMap = (
      out: Map<string, { rule: RuleDef; sources: Set<string> }>,
      ruleId: string | null | undefined,
      source: string
    ) => {
      if (!ruleId) {
        return;
      }
      const rule = ruleById.get(ruleId);
      if (!rule) {
        return;
      }
      const existing = out.get(rule.id);
      if (existing) {
        existing.sources.add(source);
        return;
      }
      out.set(rule.id, { rule, sources: new Set<string>([source]) });
    };

    const membershipTeamIds = [...(teamIdsByPlayerId.get(player.id) ?? new Set<string>([player.teamId]))];
    const getRulesForTeam = (teamId?: string | null) => {
      const out = new Map<string, { rule: RuleDef; sources: Set<string> }>();
      if (!teamId) {
        return out;
      }
      const membershipTeam = teamById.get(teamId);
      if (!membershipTeam) {
        return out;
      }
      const membershipOrganization = membershipTeam.organizationId ? orgById.get(membershipTeam.organizationId) : undefined;
      const membershipLeague = membershipTeam.leagueId ? leagueById.get(membershipTeam.leagueId) : undefined;
      attachRuleToMap(out, membershipOrganization?.restRuleId, `Organization: ${membershipOrganization?.name ?? "Unknown"}`);
      attachRuleToMap(out, membershipLeague?.restRuleId, `League: ${membershipLeague?.name ?? "Unknown"}`);
      return out;
    };

    const getRulesForAllMembershipTeams = () => {
      const out = new Map<string, { rule: RuleDef; sources: Set<string> }>();
      for (const membershipTeamId of membershipTeamIds) {
        const rulesForTeam = getRulesForTeam(membershipTeamId);
        for (const [ruleId, entry] of rulesForTeam.entries()) {
          const existing = out.get(ruleId);
          if (existing) {
            for (const source of entry.sources) {
              existing.sources.add(source);
            }
          } else {
            out.set(ruleId, { rule: entry.rule, sources: new Set(entry.sources) });
          }
        }
      }
      return out;
    };

    const mergeRuleMaps = (
      out: Map<string, { rule: RuleDef; sources: Set<string> }>,
      sourceMap: Map<string, { rule: RuleDef; sources: Set<string> }>
    ) => {
      for (const [ruleId, entry] of sourceMap.entries()) {
        const existing = out.get(ruleId);
        if (existing) {
          for (const source of entry.sources) {
            existing.sources.add(source);
          }
        } else {
          out.set(ruleId, { rule: entry.rule, sources: new Set(entry.sources) });
        }
      }
    };

    const getStrictRulesForLog = (log: PitchLogLite) => {
      const fromGameTeam = getRulesForTeam(log.teamId);
      if (fromGameTeam.size > 0) {
        return fromGameTeam;
      }

      const fromRosterTeam = getRulesForTeam(log.playerRosterTeamId);
      if (fromRosterTeam.size > 0) {
        return fromRosterTeam;
      }

      const fromMembership = getRulesForAllMembershipTeams();
      if (fromMembership.size > 0) {
        return fromMembership;
      }

      return getRulesForTeam(player.teamId);
    };

    const getRestRulesForLog = (log: PitchLogLite) => {
      const out = getRulesForAllMembershipTeams();
      mergeRuleMaps(out, getRulesForTeam(log.teamId));
      mergeRuleMaps(out, getRulesForTeam(log.playerRosterTeamId));
      if (out.size > 0) {
        return out;
      }
      return getRulesForTeam(player.teamId);
    };

    const getViolationTeamForLog = (log: PitchLogLite) => {
      const teamId = log.teamId ?? log.playerRosterTeamId ?? player.teamId;
      return {
        teamId,
        teamName: getDisplayTeamName(teamId, teamById)
      };
    };

    const applicableRules = [...getRulesForAllMembershipTeams().values()].map((entry) => ({
      rule: entry.rule,
      source: [...entry.sources].join(", ")
    }));

    let requiredRestDays = 0;
    const violationsById = new Map<string, PlayerViolation>();

    if (latestLog) {
      const latestRestRules = [...getRestRulesForLog(latestLog).values()].map((entry) => ({
        rule: entry.rule,
        source: [...entry.sources].join(", ")
      }));
      for (const { rule } of latestRestRules) {
        if (!includesPitches(rule.criteria)) {
          continue;
        }

        const levels = profilesByRuleId.get(rule.id) ?? [];
        let matchedDays = 0;
        for (const level of levels) {
          if (latestLog.pitches >= level.pitchCount) {
            matchedDays = Math.max(matchedDays, level.daysRest);
          }
        }
        requiredRestDays = Math.max(requiredRestDays, matchedDays);
      }
    }

    for (const log of playerLogs) {
      const pitchRules = [...getStrictRulesForLog(log).values()].map((entry) => ({
        rule: entry.rule,
        source: [...entry.sources].join(", ")
      }));

      for (const { rule, source } of pitchRules) {
        if (!includesPitches(rule.criteria)) {
          continue;
        }

        const levels = profilesByRuleId.get(rule.id) ?? [];
        const maxPitchLimit = levels.length > 0 ? levels[levels.length - 1].pitchCount : undefined;
        if (typeof maxPitchLimit === "number" && log.pitches > maxPitchLimit) {
          const violationTeam = getViolationTeamForLog(log);
          const id = `pitch-${rule.id}-${log.id}`;
          violationsById.set(id, {
            id,
            type: "PITCH_LIMIT",
            ruleId: rule.id,
            ruleName: rule.name,
            source,
            occurredOn: log.date,
            message: `${player.name} exceeded max pitch threshold (${log.pitches} > ${maxPitchLimit})`,
            logId: log.id,
            teamId: violationTeam.teamId,
            teamName: violationTeam.teamName
          });
        }
      }
    }

    const chronologicalLogs = [...playerLogs].sort((a, b) => a.date.localeCompare(b.date));
    for (let i = 1; i < chronologicalLogs.length; i += 1) {
      const previousLog = chronologicalLogs[i - 1];
      const currentLog = chronologicalLogs[i];
      const daysBetween = Math.max(0, calendarDayDiff(parseDate(previousLog.date), parseDate(currentLog.date)));
      const previousLogRules = [...getRestRulesForLog(previousLog).values()].map((entry) => ({
        rule: entry.rule,
        source: [...entry.sources].join(", ")
      }));
      let maxRestViolation: { rule: RuleDef; source: string; requiredRestDays: number } | null = null;

      for (const { rule, source } of previousLogRules) {
        if (!includesPitches(rule.criteria)) {
          continue;
        }

        const levels = profilesByRuleId.get(rule.id) ?? [];
        let requiredRestFromPrevious = 0;
        for (const level of levels) {
          if (previousLog.pitches >= level.pitchCount) {
            requiredRestFromPrevious = Math.max(requiredRestFromPrevious, level.daysRest);
          }
        }

        if (
          requiredRestFromPrevious > 0 &&
          daysBetween <= requiredRestFromPrevious &&
          (!maxRestViolation || requiredRestFromPrevious > maxRestViolation.requiredRestDays)
        ) {
          maxRestViolation = {
            rule,
            source,
            requiredRestDays: requiredRestFromPrevious
          };
        }
      }

      if (maxRestViolation) {
        const violationTeam = getViolationTeamForLog(currentLog);
        const id = `rest-${maxRestViolation.rule.id}-${currentLog.id}`;
        violationsById.set(id, {
          id,
          type: "REST_INTERVAL",
          ruleId: maxRestViolation.rule.id,
          ruleName: maxRestViolation.rule.name,
          source: maxRestViolation.source,
          occurredOn: currentLog.date,
            message: `${player.name} pitched again after ${daysBetween} day(s); ${maxRestViolation.requiredRestDays} full rest day(s) required from outing on ${previousLog.date}`,
            logId: currentLog.id,
            teamId: violationTeam.teamId,
            teamName: violationTeam.teamName
        });
      }
    }

    const weekLogs = playerLogs.filter((log) => {
      const day = parseDate(log.date);
      return day >= weekStart && day <= weekEnd;
    });

    const outsRules = applicableRules.filter(
      ({ rule }) => includesOuts(rule.criteria) && typeof rule.weeklyOutsLimit === "number" && (rule.weeklyOutsLimit as number) > 0
    );
    let weeklyOutsBlockedUntil: Date | null = null;
    let hasWeeklyOutsBlock = false;

    for (const { rule, source } of outsRules) {
      const matchingWeekLogs = weekLogs.filter((log) => getStrictRulesForLog(log).has(rule.id));
      const outsThisWeek = matchingWeekLogs.reduce((sum, log) => sum + (log.outsRecorded ?? 0), 0);
      const limit = rule.weeklyOutsLimit as number;
      if (outsThisWeek > limit) {
        hasWeeklyOutsBlock = true;
        const blockUntil = addDays(weekEnd, 1);
        if (!weeklyOutsBlockedUntil || blockUntil > weeklyOutsBlockedUntil) {
          weeklyOutsBlockedUntil = blockUntil;
        }
        const violationLog = [...matchingWeekLogs].sort((a, b) => b.date.localeCompare(a.date))[0];
        const violationTeam = violationLog ? getViolationTeamForLog(violationLog) : { teamId: player.teamId, teamName: getDisplayTeamName(player.teamId, teamById) };
        const id = `outs-${rule.id}`;
        violationsById.set(id, {
          id,
          type: "WEEKLY_OUTS",
          ruleId: rule.id,
          ruleName: rule.name,
          source,
          occurredOn: formatDate(weekEnd),
          message: `${player.name} exceeded weekly outs limit (${outsThisWeek} > ${limit})`,
          logId: violationLog?.id,
          teamId: violationTeam.teamId,
          teamName: violationTeam.teamName
        });
      }
    }

    const latestDate = latestLog ? parseDate(latestLog.date) : todayDate;
    const pitchRestAvailableOnDate = latestLog ? addDays(latestDate, requiredRestDays + (requiredRestDays > 0 ? 1 : 0)) : todayDate;
    const availableOnDate =
      weeklyOutsBlockedUntil && weeklyOutsBlockedUntil > pitchRestAvailableOnDate
        ? weeklyOutsBlockedUntil
        : pitchRestAvailableOnDate;
    const availableOn = formatDate(availableOnDate);
    const daysRemaining = latestLog ? Math.max(0, calendarDayDiff(todayDate, availableOnDate)) : 0;
    const daysElapsed = latestLog ? Math.max(0, calendarDayDiff(latestDate, todayDate)) : requiredRestDays;
    const restProgressPct =
      requiredRestDays > 0 ? Math.min(100, Math.max(0, Math.round((daysElapsed / requiredRestDays) * 100))) : 100;
    const available = !latestLog || daysRemaining === 0;
    const restBlocking = requiredRestDays > 0 && pitchRestAvailableOnDate > todayDate;
    const reason = available
      ? "Available"
      : [
          restBlocking ? `Rest required through ${formatDate(pitchRestAvailableOnDate)}` : "",
          hasWeeklyOutsBlock ? `Weekly outs limit reached, unavailable until ${availableOn}` : ""
        ]
          .filter(Boolean)
          .join(" | ") + ` (${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left)`;

    return {
      playerId: player.id,
      playerName: player.name,
      teamName: team?.name ?? "Unknown team",
      available,
      availableOn,
      reason,
      daysRemaining,
      restTotalDays: requiredRestDays,
      restProgressPct,
      violations: [...violationsById.values()]
    };
  });
};
